import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  Chip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import ClearIcon from '@mui/icons-material/Clear';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function formatDate(dateValue) {
  if (!dateValue) return '-';
  const date = new Date(dateValue);
  return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDateTime(dateValue) {
  if (!dateValue) return '-';
  const date = new Date(dateValue);
  return date.toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function SignedCheckinsPage() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [filterRoom, setFilterRoom] = useState('');
  const [filterStart, setFilterStart] = useState('');
  const [filterEnd, setFilterEnd] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchSignedCheckins();
  }, []);

  const fetchSignedCheckins = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/bookings/signed-checkins`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        throw new Error('No se pudieron cargar los check-ins firmados.');
      }
      const data = await response.json();
      setRecords(data);
    } catch (err) {
      console.error('Error cargando check-ins firmados:', err);
      setError(err.message || 'Error al cargar los check-ins firmados.');
    } finally {
      setLoading(false);
    }
  };

  const downloadSignedCheckin = async (bookingId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}/checkin/pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'No se pudo descargar el check-in firmado.');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CheckinFirmado_${bookingId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error descargando check-in firmado:', err);
      setError(err.message || 'Error al descargar el check-in firmado.');
    }
  };

  const deleteSignedCheckin = async (bookingId) => {
    setDeleting(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}/checkin`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'No se pudo eliminar el check-in firmado.');
      }
      setDeleteConfirm(null);
      await fetchSignedCheckins();
    } catch (err) {
      console.error('Error eliminando check-in firmado:', err);
      setError(err.message || 'Error al eliminar el check-in firmado.');
    } finally {
      setDeleting(false);
    }
  };

  const roomOptions = useMemo(
    () => Array.from(new Set(records.map((record) => record.roomName).filter(Boolean))),
    [records]
  );

  const filteredRecords = useMemo(() => {
    return records.filter((record) => {
      const normalizedText = filterText.trim().toLowerCase();
      const textMatch =
        !normalizedText ||
        `${record.bookingId}`.toLowerCase().includes(normalizedText) ||
        `${record.guestInfo?.firstName || ''} ${record.guestInfo?.lastName || ''}`.toLowerCase().includes(normalizedText) ||
        `${record.guestInfo?.email || ''}`.toLowerCase().includes(normalizedText) ||
        `${record.roomName || ''}`.toLowerCase().includes(normalizedText);

      const roomMatch = !filterRoom || record.roomName === filterRoom;

      const checkInDate = record.checkIn ? new Date(record.checkIn) : null;
      const startDate = filterStart ? new Date(filterStart) : null;
      const endDate = filterEnd ? new Date(filterEnd) : null;

      const dateMatch =
        (!startDate || (checkInDate && checkInDate >= startDate)) &&
        (!endDate || (checkInDate && checkInDate <= endDate));

      return textMatch && roomMatch && dateMatch;
    });
  }, [records, filterText, filterRoom, filterStart, filterEnd]);

  const resetFilters = () => {
    setFilterText('');
    setFilterRoom('');
    setFilterStart('');
    setFilterEnd('');
  };

  return (
    <Box>
      <Stack spacing={3}>
        <Paper variant="outlined" sx={{ p: 3, bgcolor: '#faf8f2' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                Check-ins firmados
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 640 }}>
                Revisa, filtra y descarga los registros de check-in firmados. Aquí puedes consultar las firmas, la fecha de llegada y eliminar check-ins obsoletos de forma segura.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip label={`Total firmados: ${records.length}`} color="primary" />
              <Chip label={`Mostrando: ${filteredRecords.length}`} variant="outlined" />
            </Stack>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
            Filtros inteligentes
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-end">
            <TextField
              fullWidth
              value={filterText}
              label="Buscar por reserva, huésped o habitación"
              placeholder="Ej. 12345, María, Master Suite"
              onChange={(event) => setFilterText(event.target.value)}
              size="small"
            />
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel id="signed-checkins-room-label">Habitación</InputLabel>
              <Select
                labelId="signed-checkins-room-label"
                value={filterRoom}
                label="Habitación"
                onChange={(event) => setFilterRoom(event.target.value)}
              >
                <MenuItem value="">Todas</MenuItem>
                {roomOptions.map((room) => (
                  <MenuItem key={room} value={room}>
                    {room}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Check-in desde"
              type="date"
              value={filterStart}
              onChange={(event) => setFilterStart(event.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label="Check-in hasta"
              type="date"
              value={filterEnd}
              onChange={(event) => setFilterEnd(event.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ClearIcon />}
              onClick={resetFilters}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Limpiar filtros
            </Button>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Reserva</TableCell>
                    <TableCell>Huésped</TableCell>
                    <TableCell>Habitación</TableCell>
                    <TableCell>Check-in</TableCell>
                    <TableCell>Check-out</TableCell>
                    <TableCell>Firmado</TableCell>
                    <TableCell>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRecords.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <Typography color="text.secondary">
                          No se encontraron registros con esos criterios. Ajusta los filtros para ampliar la búsqueda.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRecords.map((record) => (
                      <TableRow key={record._id} hover>
                        <TableCell>{record.bookingId}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="600">
                            {record.guestInfo?.firstName || '-'} {record.guestInfo?.lastName || ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {record.guestInfo?.email || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>{record.roomName || '-'}</TableCell>
                        <TableCell>{formatDate(record.checkIn)}</TableCell>
                        <TableCell>{formatDate(record.checkOut)}</TableCell>
                        <TableCell>{formatDateTime(record.checkinSignedAt)}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <Tooltip title="Descargar PDF firmado">
                              <IconButton size="small" onClick={() => downloadSignedCheckin(record.bookingId)}>
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar check-in firmado">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setDeleteConfirm(record.bookingId)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Stack>

      {deleteConfirm && (
        <Paper variant="outlined" sx={{ p: 3, mt: 2, bgcolor: '#fff3f3' }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            Confirmar eliminación
          </Typography>
          <Typography sx={{ mb: 2 }}>
            Estás a punto de eliminar permanentemente el check-in firmado de la reserva <strong>{deleteConfirm}</strong>. Esta acción no se puede deshacer.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <Button variant="outlined" onClick={() => setDeleteConfirm(null)} disabled={deleting}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => deleteSignedCheckin(deleteConfirm)}
              disabled={deleting}
            >
              {deleting ? 'Eliminando...' : 'Eliminar registro'}
            </Button>
          </Stack>
        </Paper>
      )}
    </Box>
  );
}
